import { lazy, Suspense, useEffect } from 'react'
import { TrialBanner } from './components/TrialBanner'
import { DemoBanner } from './components/DemoBanner'
import { useTranslation } from 'react-i18next'
import { isRtlLanguage } from './i18n'
import { QueryClientProvider } from '@tanstack/react-query'
import { Routes, Route, Navigate, useParams } from 'react-router-dom'
import { queryClient } from './lib/queryClient'
import Login from './pages/Login'
import { AuthProvider, useAuth } from './auth/AuthProvider'
import { useHasPermission } from './hooks/usePermissions'
import { ToastProvider } from './components/ui/Toast'
import AppShell from './components/layout/AppShell'
import { ErrorBoundary } from './components/ErrorBoundary'
import { UpdateBanner } from './components/UpdateBanner'

// ── Lazy-loaded pages (code-split per route) ────────────────────────────────
const Dashboard = lazy(() => import('./pages/Dashboard'))
const Calendar = lazy(() => import('./pages/Calendar'))
const Patients = lazy(() => import('./pages/Patients'))
const PatientNew = lazy(() => import('./pages/PatientNew'))
const PatientDetail = lazy(() => import('./pages/PatientDetail'))
const MedicalRecordEdit = lazy(() => import('./pages/MedicalRecordEdit'))
const PatientDocumentUploader = lazy(() => import('./components/PatientDocumentUploader'))
const PatientPrescriptions = lazy(() => import('./pages/PatientPrescriptions'))
const PatientPrescriptionPrint = lazy(() => import('./pages/PatientPrescriptionPrint'))
const Doctors = lazy(() => import('./pages/Doctors'))
const DoctorNew = lazy(() => import('./pages/DoctorNew'))
const DoctorDetail = lazy(() => import('./pages/DoctorDetail'))
const Operations = lazy(() => import('./pages/Operations'))
const OperationDetail = lazy(() => import('./pages/OperationDetail'))
const Emergency = lazy(() => import('./pages/Emergency'))
const EmergencyBoard = lazy(() => import('./pages/EmergencyBoard'))
const EmergencyDischarge = lazy(() => import('./pages/EmergencyDischarge'))
const EmergencyTransfer = lazy(() => import('./pages/EmergencyTransfer'))
const EmergencyCashier = lazy(() => import('./pages/EmergencyCashier'))
const DoctorAccount = lazy(() => import('./pages/DoctorAccount'))
const EmergencyAdmit = lazy(() => import('./pages/EmergencyAdmit'))
const EmergencyIdentify = lazy(() => import('./pages/EmergencyIdentify'))
const FacilityManagement = lazy(() => import('./pages/FacilityManagement'))
const InpatientCashier = lazy(() => import('./pages/InpatientCashier'))
const Resources = lazy(() => import('./pages/Resources'))
const ResourceAdd = lazy(() => import('./pages/ResourceAdd'))
const Scheduling = lazy(() => import('./pages/Scheduling'))
const Billing = lazy(() => import('./pages/Billing'))
const Accounting = lazy(() => import('./pages/Accounting'))
const BillingAnalyticsPanel = lazy(() => import('./components/billing/BillingAnalyticsPanel'))
const Services = lazy(() => import('./pages/Services'))
const WorkingHours = lazy(() => import('./pages/WorkingHours'))
const Absences = lazy(() => import('./pages/Absences'))
const Tasks = lazy(() => import('./pages/Tasks'))
// Sprint R Phase R4: Owner-Sicht Klinik-Ausgaben
const Expenses = lazy(() => import('./pages/Expenses'))
// Sprint R Phase R5: Owner-Dashboard
const OwnerDashboard = lazy(() => import('./pages/OwnerDashboard'))
const Documents = lazy(() => import('./pages/Documents'))
const Settings = lazy(() => import('./pages/Settings'))
const Prescriptions = lazy(() => import('./pages/Prescriptions'))
const Lab = lazy(() => import('./pages/Lab'))
const LabOrderDetail = lazy(() => import('./pages/LabOrderDetail'))
const LabOrderPrint = lazy(() => import('./pages/LabOrderPrint'))
const LabSettings = lazy(() => import('./pages/LabSettings'))
const ExaminationTemplates = lazy(() => import('./pages/ExaminationTemplates'))
const Imaging = lazy(() => import('./pages/Imaging'))
const ImagingStudyDetail = lazy(() => import('./pages/ImagingStudyDetail'))
const Messaging = lazy(() => import('./pages/Messaging'))
const MedicalReports = lazy(() => import('./pages/MedicalReports'))
const Translator = lazy(() => import('./pages/Translator'))
const WaitingRoom = lazy(() => import('./pages/WaitingRoom'))
const WaitingDisplay = lazy(() => import('./pages/WaitingDisplay'))
const SpecialtyRecallPage = lazy(() => import('./modules/specialties/pages/SpecialtyRecallPage'))
const SpecialtyDashboardPage = lazy(() => import('./modules/specialties/pages/SpecialtyDashboardPage'))
const ShiftPlanning = lazy(() => import('./pages/ShiftPlanning'))
const Triage = lazy(() => import('./pages/Triage'))
const Notifications = lazy(() => import('./pages/Notifications'))
const AuditLog = lazy(() => import('./pages/AuditLog'))
const DataImport = lazy(() => import('./pages/DataImport'))
const DataExport = lazy(() => import('./pages/DataExport'))
const Guide = lazy(() => import('./pages/Guide'))
const Feedback = lazy(() => import('./pages/Feedback'))
const PatientPortal = lazy(() => import('./pages/PatientPortal'))
const OnboardingPage = lazy(() => import('./modules/onboarding/OnboardingPage'))
const LicenseActivation = lazy(() => import('./pages/LicenseActivation'))
const IntakeForm = lazy(() => import('./pages/IntakeForm'))
const Analytics = lazy(() => import('./pages/Analytics'))
const PublicBooking = lazy(() => import('./pages/PublicBooking'))
const PortalLogin = lazy(() => import('./pages/PortalLogin'))
const PortalHome = lazy(() => import('./pages/PortalHome'))
const RiskAssessmentPage = lazy(() => import('./features/risk-assessment/RiskAssessmentPage'))
const DdxAssessment = lazy(() => import('./pages/DdxAssessment'))
const ReactQueryDevtools = lazy(() =>
  import('@tanstack/react-query-devtools').then(m => ({ default: m.ReactQueryDevtools }))
)

function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <div className="flex flex-col items-center gap-3">
        <div className="w-8 h-8 border-2 border-zinc-200 dark:border-zinc-700 border-t-blue-500 rounded-full animate-spin" />
        <span className="text-sm text-zinc-400 dark:text-zinc-500">جاري التحميل...</span>
      </div>
    </div>
  )
}

function RequireAuth({ children }: { children: JSX.Element }) {
  const { user, initializing } = useAuth()
  if (initializing) return null
  if (!user) return <Navigate to="/login" replace />
  return children
}

function RequirePermission({ codename, children }: Readonly<{ codename: string; children: JSX.Element }>) {
  const { user, initializing } = useAuth()
  const allowed = useHasPermission(codename)
  if (initializing) return null
  if (!user) return <Navigate to="/login" replace />
  if (!allowed) return <Navigate to="/" replace />
  return children
}

function RedirectIfAuth({ children }: { children: JSX.Element }) {
  const { user, initializing } = useAuth()
  if (initializing) return null
  if (user) return <Navigate to="/" replace />
  return children
}

// Stub-Routen für patientenspezifisches Anlegen. Bis es eigene
// LabOrderNew/ImagingStudyNew-Pages gibt, leiten wir auf die Listen-
// pages mit ?patient= weiter; deren NewOrderModal/NewStudyModal liest
// den Param und befüllt den Patienten vor.
function LabOrderNewRedirect() {
  const { id } = useParams<{ id: string }>()
  return <Navigate to={`/lab?patient=${id ?? ''}`} replace />
}

function ImagingStudyNewRedirect() {
  const { id } = useParams<{ id: string }>()
  return <Navigate to={`/imaging?patient=${id ?? ''}`} replace />
}

/** Returns true for routes that render standalone (no sidebar/topbar/padding). */
function isStandaloneRoute() {
  const loc = window.location.pathname
  return loc.startsWith('/display/') || loc.startsWith('/portal') || loc.startsWith('/onboarding') || loc.startsWith('/book/')
    // Print views (lab order, prescription, …) are full-page documents — the
    // app chrome (sidebar/topnav icons) must not surface on screen or in print.
    || loc.endsWith('/print')
}

function AuthenticatedChrome({ children }: { children?: React.ReactNode }) {
  const { user, initializing } = useAuth()
  if (isStandaloneRoute()) return <>{children}</>
  if (initializing || !user) return <>{children}</>
  return <AppShell>{children}</AppShell>
}

export default function App() {
  const { i18n } = useTranslation()

  useEffect(() => {
    const dir = isRtlLanguage(i18n.language) ? 'rtl' : 'ltr'
    document.documentElement.dir = dir
    document.documentElement.lang = i18n.language
  }, [i18n.language])

  // Browser-tab title is now set per-route in AppShell (resolveRouteTitleKey)
  // so each open page is identifiable. The app version stays in the footer.

  return (
    <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
          <ToastProvider>
          <DemoBanner />
          <UpdateBanner />
          {/* Public routes without sidebar/chrome */}
          <Routes>
            <Route path="/display/:token" element={<Suspense fallback={<PageLoader />}><WaitingDisplay /></Suspense>} />
            <Route path="/display" element={<Suspense fallback={<PageLoader />}><WaitingDisplay /></Suspense>} />
            <Route path="/portal" element={<Suspense fallback={<PageLoader />}><PatientPortal /></Suspense>} />
            <Route path="/portal/login" element={<Suspense fallback={<PageLoader />}><PortalLogin /></Suspense>} />
            <Route path="/portal/home" element={<Suspense fallback={<PageLoader />}><PortalHome /></Suspense>} />
            <Route path="/onboarding" element={<RequireAuth><Suspense fallback={<PageLoader />}><OnboardingPage /></Suspense></RequireAuth>} />
            <Route path="/license" element={<Suspense fallback={<PageLoader />}><LicenseActivation /></Suspense>} />
            <Route path="/intake/:token" element={<Suspense fallback={<PageLoader />}><IntakeForm /></Suspense>} />
            <Route path="/book/:slug" element={<Suspense fallback={<PageLoader />}><PublicBooking /></Suspense>} />
            <Route path="*" element={
          <AuthenticatedChrome>
          <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/login" element={<RedirectIfAuth><Login /></RedirectIfAuth>} />
            <Route path="/" element={<RequireAuth><Dashboard /></RequireAuth>} />
            <Route path="/calendar" element={<RequireAuth><Calendar /></RequireAuth>} />
            <Route path="/patients" element={<RequireAuth><Patients /></RequireAuth>} />
            <Route path="/patients/new" element={<RequireAuth><PatientNew /></RequireAuth>} />
            <Route path="/patients/:id/documents" element={<RequireAuth><PatientDocumentUploader /></RequireAuth>} />
            <Route path="/patients/:id/prescriptions/:rxId/print" element={<RequireAuth><PatientPrescriptionPrint /></RequireAuth>} />
            <Route path="/patients/:id/prescriptions" element={<RequireAuth><PatientPrescriptions /></RequireAuth>} />
            <Route path="/patients/:id/medical-records/new" element={<RequireAuth><MedicalRecordEdit /></RequireAuth>} />
            <Route path="/patients/:id/medical-records/:mrId/edit" element={<RequireAuth><MedicalRecordEdit /></RequireAuth>} />
            <Route path="/patients/:id/*" element={<RequireAuth><PatientDetail /></RequireAuth>} />
            <Route path="/doctors" element={<RequireAuth><Doctors /></RequireAuth>} />
            <Route path="/doctors/new" element={<RequireAuth><DoctorNew /></RequireAuth>} />
            <Route path="/doctors/:id" element={<RequireAuth><DoctorDetail /></RequireAuth>} />
            <Route path="/operations" element={<RequireAuth><Operations /></RequireAuth>} />
            <Route path="/operations/:id" element={<RequireAuth><OperationDetail /></RequireAuth>} />
            <Route path="/emergency" element={<RequireAuth><Emergency /></RequireAuth>} />
            <Route path="/emergency/board" element={<RequireAuth><EmergencyBoard /></RequireAuth>} />
            <Route path="/emergency/discharge/:id" element={<RequireAuth><EmergencyDischarge /></RequireAuth>} />
            <Route path="/emergency/transfer/:id" element={<RequireAuth><EmergencyTransfer /></RequireAuth>} />
            <Route path="/emergency/cashier/:id" element={<RequireAuth><EmergencyCashier /></RequireAuth>} />
            <Route path="/doctor-account" element={<RequireAuth><DoctorAccount /></RequireAuth>} />
            <Route path="/emergency/admit/:id" element={<RequireAuth><EmergencyAdmit /></RequireAuth>} />
            <Route path="/emergency/identify" element={<RequireAuth><EmergencyIdentify /></RequireAuth>} />
            <Route path="/facility" element={<RequireAuth><FacilityManagement /></RequireAuth>} />
            <Route path="/inpatient/cashier/:id" element={<RequireAuth><InpatientCashier /></RequireAuth>} />
            <Route path="/scheduling" element={<RequireAuth><Scheduling /></RequireAuth>} />
            <Route path="/resources" element={<RequireAuth><Resources /></RequireAuth>} />
            <Route path="/resources/new" element={<RequireAuth><ResourceAdd /></RequireAuth>} />
            <Route path="/billing" element={<RequireAuth><Billing /></RequireAuth>} />
            {/* D46 F1: UAS general ledger; the page gates itself on accounting_enabled + accounting.view */}
            <Route path="/accounting" element={<RequireAuth><Accounting /></RequireAuth>} />
            {/* Sprint R Phase R4: Klinik-Ausgaben (Owner-Sicht) */}
            <Route path="/expenses" element={<RequireAuth><Expenses /></RequireAuth>} />
            {/* Sprint R Phase R5: Owner-Dashboard */}
            <Route path="/owner-dashboard" element={<RequireAuth><OwnerDashboard /></RequireAuth>} />
            <Route path="/billing/analytics" element={<RequireAuth><BillingAnalyticsPanel /></RequireAuth>} />
            <Route path="/services" element={<RequireAuth><Services /></RequireAuth>} />
            <Route path="/doctor-hours" element={<RequireAuth><WorkingHours /></RequireAuth>} />
            <Route path="/doctor-absences" element={<RequireAuth><Absences /></RequireAuth>} />
            <Route path="/tasks" element={<RequireAuth><Tasks /></RequireAuth>} />
            <Route path="/documents" element={<RequireAuth><Documents /></RequireAuth>} />
            <Route path="/prescriptions" element={<RequireAuth><Prescriptions /></RequireAuth>} />
            <Route path="/lab" element={<RequireAuth><Lab /></RequireAuth>} />
            <Route path="/lab/orders/:id" element={<RequireAuth><LabOrderDetail /></RequireAuth>} />
            <Route path="/lab/orders/:id/print" element={<RequireAuth><LabOrderPrint /></RequireAuth>} />
            <Route path="/lab/settings" element={<RequireAuth><LabSettings /></RequireAuth>} />
            <Route path="/settings/examination-templates" element={<RequireAuth><ExaminationTemplates /></RequireAuth>} />
            <Route path="/patients/:id/lab/new" element={<RequireAuth><LabOrderNewRedirect /></RequireAuth>} />
            <Route path="/imaging" element={<RequireAuth><Imaging /></RequireAuth>} />
            <Route path="/imaging/studies/:id" element={<RequireAuth><ImagingStudyDetail /></RequireAuth>} />
            <Route path="/patients/:id/imaging/new" element={<RequireAuth><ImagingStudyNewRedirect /></RequireAuth>} />
            <Route path="/messaging" element={<RequireAuth><Messaging /></RequireAuth>} />
            <Route path="/medical-reports" element={<RequireAuth><MedicalReports /></RequireAuth>} />
            <Route path="/translator" element={<RequireAuth><Translator /></RequireAuth>} />
            <Route path="/waiting-room" element={<RequireAuth><WaitingRoom /></RequireAuth>} />
            {/* display + portal moved outside chrome */}
            <Route path="/specialty-recalls" element={<RequireAuth><SpecialtyRecallPage /></RequireAuth>} />
            <Route path="/specialty-dashboard" element={<RequireAuth><SpecialtyDashboardPage /></RequireAuth>} />
            <Route path="/shift-planning" element={<RequireAuth><ShiftPlanning /></RequireAuth>} />
            <Route path="/triage" element={<RequireAuth><Triage /></RequireAuth>} />
            <Route path="/risk-assessment/:appointmentId" element={<RequirePermission codename="triage.access_risk_assessment"><RiskAssessmentPage /></RequirePermission>} />
            <Route path="/patients/:patientId/risk-assessment" element={<RequirePermission codename="triage.access_risk_assessment"><RiskAssessmentPage /></RequirePermission>} />
            <Route path="/patients/:id/ddx-assessment" element={<RequireAuth><DdxAssessment /></RequireAuth>} />
            <Route path="/notifications" element={<RequireAuth><Notifications /></RequireAuth>} />
            <Route path="/audit-log" element={<RequireAuth><AuditLog /></RequireAuth>} />
            <Route path="/import" element={<RequireAuth><DataImport /></RequireAuth>} />
            <Route path="/export" element={<RequireAuth><DataExport /></RequireAuth>} />
            <Route path="/analytics" element={<RequireAuth><Analytics /></RequireAuth>} />
            <Route path="/settings" element={<RequirePermission codename="admin.settings_access"><Settings /></RequirePermission>} />
            <Route path="/guide" element={<RequireAuth><Guide /></RequireAuth>} />
            <Route path="/feedback" element={<RequireAuth><Feedback /></RequireAuth>} />
          </Routes>
          </Suspense>
          </AuthenticatedChrome>
          } />
          </Routes>
        </ToastProvider>
      </AuthProvider>
      <TrialBanner />
    </QueryClientProvider>
    </ErrorBoundary>
  )
}
